import { ArrowDown, ArrowUp } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { type AiRoute, type AiRouteTest, aiApi, costLabel, durationLabel, moved, num, priceLabel, regionLabel, routeLabel, routesByModel } from '@/lib/aiAdmin';
import { formatCount } from '@/lib/format';

import { AddModelDialog } from './AddModelDialog';
import { CENTER, TITLE, columns, confirmedWrite, errorText, SectionLabel, Toggle } from './common';

const APPLIES = 'It applies to every organization within about 30 seconds.';

/** Each model with its routes in order, buttons to move each route, and a switch for each route. */
export function ModelsTab({ routes, onRoutes, reload }: { routes: AiRoute[]; onRoutes: (routes: AiRoute[]) => void; reload: () => void }) {
  const [busy, setBusy] = useState<string | undefined>();
  const [adding, setAdding] = useState(false);
  const [tests, setTests] = useState<Record<string, AiRouteTest>>({});

  const test = async (route: AiRoute) => {
    setBusy(route.id);
    try {
      const result = await aiApi.testRoute(route.id);
      setTests((now) => ({ ...now, [route.id]: result }));
    } catch (error) {
      toast.error(errorText(error, 'The test failed'));
    }
    setBusy(undefined);
  };

  /** Shows the order `ai` answered, then reads the routes again for the health numbers. */
  const applyOrder = (modelId: string, ids: string[] | undefined) => {
    if (ids?.length) {
      const rank = new Map(ids.map((id, i) => [id, i]));
      onRoutes(routes.map((route) => (route.modelId === modelId ? { ...route, priority: rank.get(route.id) ?? route.priority } : route)));
    }
    reload();
  };

  const write = async (key: string, question: string, run: () => Promise<{ routeIds?: string[] } | void>, modelId: string, done: string) => {
    setBusy(key);
    let ids: string[] | undefined;
    const ok = await confirmedWrite(question, async () => void (ids = (await run())?.routeIds), done, 'The change failed');
    if (ok) applyOrder(modelId, ids);
    setBusy(undefined);
  };

  const setEnabled = async (route: AiRoute, enabled: boolean) => {
    setBusy(route.id);
    const ok = await confirmedWrite(
      `Turn ${routeLabel(route)} ${enabled ? 'on' : 'off'}? ${APPLIES}`,
      async () => {
        // `ai` serves a route only when its model is on too. A slash in the id marks a route added here. The model switch is ours only when
        // every route of the model has one: a seeded route keeps the model's own switch, and the API answers 409 for that model.
        const addedHere = routes.filter((candidate) => candidate.modelId === route.modelId).every((candidate) => candidate.id.includes('/'));
        if (enabled && addedHere) await aiApi.setModelEnabled(route.modelId, true);
        await aiApi.setRouteEnabled(route.id, enabled);
      },
      `${routeLabel(route)} is ${enabled ? 'on' : 'off'}`,
      'The change failed',
    );
    if (ok) onRoutes(routes.map((candidate) => (candidate.id === route.id ? { ...candidate, enabled } : candidate)));
    setBusy(undefined);
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <Button type="button" size="sm" onClick={() => setAdding(true)}>
          Add model
        </Button>
      </div>
      {adding ? (
        <AddModelDialog
          modelIds={routes.map((route) => route.modelId)}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
      {routesByModel(routes).map(({ modelId, routes: list }) => {
        const ids = list.map((route) => route.id);
        return (
          <section key={modelId}>
            <SectionLabel>
              {modelId}
            </SectionLabel>
            <DataTable>
              <DataTableHead columns={columns(['Route', 'On', 'Order', 'Price in / out per 1M', 'Last hour', ''])} />
              <tbody>
                {list.map((route, index) => (
                  <DataTableRow key={route.id}>
                    <DataTableCell className={TITLE}>
                      <span className="mr-2 text-muted-foreground tabular-nums">{index + 1}</span>
                      {routeLabel(route)}
                      <span className="mt-0.5 block pl-5 text-xs font-normal text-muted-foreground">
                        {regionLabel(route.regionClass)}, {route.retentionClass}
                      </span>
                    </DataTableCell>
                    <DataTableCell className={CENTER}>
                      <Toggle checked={route.enabled === true} label={`${routeLabel(route)} on`} disabled={busy !== undefined} onChange={(next) => void setEnabled(route, next)} />
                    </DataTableCell>
                    <DataTableCell className={CENTER}>
                      <div className="inline-flex gap-1">
                        {([-1, 1] as const).map((step) => (
                          <Button
                            key={step}
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 px-0"
                            aria-label={`Move ${routeLabel(route)} ${step < 0 ? 'up' : 'down'}`}
                            disabled={busy !== undefined || moved(ids, index, step) === ids}
                            onClick={() =>
                              void write(
                                modelId,
                                `Move ${routeLabel(route)} ${step < 0 ? 'up' : 'down'}? ${APPLIES}`,
                                () => aiApi.setOrder(modelId, { routeIds: moved(ids, index, step) }),
                                modelId,
                                `${modelId} has a new order`,
                              )
                            }
                          >
                            {step < 0 ? <ArrowUp className="h-4 w-4" aria-hidden="true" /> : <ArrowDown className="h-4 w-4" aria-hidden="true" />}
                          </Button>
                        ))}
                      </div>
                    </DataTableCell>
                    <DataTableCell className={`${CENTER} tabular-nums`}>
                      {priceLabel(route.price?.inputMicrosPerMtok)} / {priceLabel(route.price?.outputMicrosPerMtok)}
                    </DataTableCell>
                    <DataTableCell className={`${CENTER} tabular-nums`}>
                      {formatCount(num(route.health?.calls))} calls, {formatCount(num(route.health?.errors))} {num(route.health?.errors) === 1 ? 'error' : 'errors'}
                      <span className="mt-0.5 block text-xs text-muted-foreground">p50 {durationLabel(route.health?.p50LatencyMs)}</span>
                    </DataTableCell>
                    <DataTableCell className={CENTER}>
                      {route.enabled === true ? null : (
                        <div>
                          <div className="inline-flex gap-2">
                            <Button type="button" size="sm" variant="outline" disabled={busy !== undefined} onClick={() => void test(route)}>
                              Test
                            </Button>
                            <Button type="button" size="sm" disabled={busy !== undefined} onClick={() => void setEnabled(route, true)}>
                              Turn on
                            </Button>
                          </div>
                          {tests[route.id] ? (
                            <span className="mt-0.5 block text-xs text-muted-foreground">
                              {tests[route.id]!.status === 'ok' ? 'ok' : `failed${tests[route.id]!.errorClass ? `: ${tests[route.id]!.errorClass}` : ''}`}, {durationLabel(tests[route.id]!.latencyMs)}
                              {tests[route.id]!.costUsd ? `, ${costLabel(tests[route.id]!.costUsd)}` : ''}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </tbody>
            </DataTable>
          </section>
        );
      })}
      {routes.length === 0 ? <p className="text-sm text-muted-foreground">ai has no routes.</p> : null}
    </div>
  );
}
